/**
 * Pure helper functions for the OrdenExamenes flat view.
 * Extracted for testability with PBT.
 */

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

/**
 * Maps exam status to badge variant for visual styling.
 * - VALIDADO → default (success/green)
 * - CANCELADO → destructive (red)
 * - CON_RESULTADO → secondary (blue/gray)
 * - EN_PROCESO, PENDIENTE, unknown → outline
 */
export function getEstadoExamenBadgeVariant(estado: string): BadgeVariant {
  switch (estado) {
    case "VALIDADO":
      return "default";
    case "CANCELADO":
      return "destructive";
    case "CON_RESULTADO":
      return "secondary";
    case "EN_PROCESO":
      return "outline";
    default:
      return "outline";
  }
}

/**
 * Maps order status to badge variant for visual styling.
 * - FINALIZADA → default (success/green)
 * - BAJA → destructive (red)
 * - EN_EXTRACCION → outline
 * - PENDIENTE, CON_RESULTADO, unknown → secondary
 */
export function getEstadoOrdenBadgeVariant(estado: string): BadgeVariant {
  switch (estado) {
    case "FINALIZADA":
      return "default";
    case "BAJA":
      return "destructive";
    case "EN_EXTRACCION":
      return "outline";
    default:
      return "secondary";
  }
}

/**
 * Maps order priority to badge variant for visual styling.
 * - STAT → destructive (red)
 * - URGENTE → secondary (amber/warning)
 * - NORMAL, unknown → outline
 */
export function getPrioridadBadgeVariant(prioridad: string): BadgeVariant {
  switch (prioridad) {
    case "STAT":
      return "destructive";
    case "URGENTE":
      return "secondary";
    default:
      return "outline";
  }
}

/**
 * Formats a price number to Honduran Lempira display string.
 * Always shows 2 decimal places with "L " prefix.
 */
export function formatPrecioLempiras(precio: number): string {
  return `L ${precio.toFixed(2)}`;
}

/** All valid exam status values used in the OrdenExamenes view. */
export const EXAM_ESTADOS = [
  "PENDIENTE",
  "EN_PROCESO",
  "CON_RESULTADO",
  "VALIDADO",
  "CANCELADO",
] as const;

/** All valid order status values. */
export const ORDEN_ESTADOS = [
  "EN_EXTRACCION",
  "PENDIENTE",
  "CON_RESULTADO",
  "FINALIZADA",
  "BAJA",
] as const;

/** All valid laboratory area values for filtering. */
export const AREAS_LABORATORIO = [
  "HEMATOLOGIA",
  "QUIMICA",
  "SEROLOGIA",
  "INMUNOLOGIA",
  "URINALISIS",
  "COAGULACION",
  "MICROBIOLOGIA",
  "PARASITOLOGIA",
  "HORMONAS",
  "MARCADORES",
  "DROGAS",
  "ESPECIAL",
] as const;

export type ExamEstado = (typeof EXAM_ESTADOS)[number];
export type OrdenEstado = (typeof ORDEN_ESTADOS)[number];
export type AreaLaboratorio = (typeof AREAS_LABORATORIO)[number];
